import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { isDateKey, MEAL_TYPES, type MealType } from "@mealplan/shared";
import { appRoute } from "./route";
import { PlannerView } from "@/features/planner/components/planner-view";
import { PageContainer } from "@fleet/ui";
import { AppErrorBoundary } from "@/components/error-boundary";

interface PlannerSearch {
  week?: string;
  /** Day for "pick recipe" picker (`?add=YYYY-MM-DD`). */
  add?: string;
  /** Meal for the picker (`?meal=breakfast|lunch|dinner|snack`). */
  meal?: MealType;
  /** Search query within picker (`?addq=`). */
  addq?: string;
}

const isMealType = (value: unknown): value is MealType =>
  typeof value === "string" && (MEAL_TYPES as readonly string[]).includes(value);

function PlannerPage() {
  const search = useSearch({ from: "/_app/planner" });
  const navigate = useNavigate();

  // Persists active week in the URL (deep-link `?week=YYYY-MM-DD`). `replace`
  // avoids inflating browser history when paging week to week.
  const syncWeek = (weekStartKey: string): void => {
    if (weekStartKey === (search().week ?? "")) {
      return;
    }
    void navigate({
      to: "/planner",
      // Switching weeks closes any open picker (its day no longer applies).
      search: { week: weekStartKey },
      replace: true,
    });
  };

  // The "choose recipe" picker is deep-linkable: `?add=<date>&meal=<meal>` opens the
  // dialog on the exact cell; `?addq=` preserves the search. Idempotent.
  const syncPicker = (picker: { date: string; meal: MealType } | null, query: string): void => {
    const current = search();
    const nextSearch = {
      ...(current.week ? { week: current.week } : {}),
      ...(picker ? { add: picker.date, meal: picker.meal } : {}),
      ...(picker && query.trim() ? { addq: query.trim() } : {}),
    } satisfies PlannerSearch;
    if (
      nextSearch.add === current.add &&
      nextSearch.meal === current.meal &&
      (nextSearch.addq ?? "") === (current.addq ?? "")
    ) {
      return;
    }
    void navigate({
      to: "/planner",
      search: nextSearch,
      replace: true,
    });
  };

  return (
    <PageContainer maxWidth="5xl">
      <AppErrorBoundary variant="page" region="planner">
        <PlannerView
          initialWeek={search().week}
          onWeekChange={syncWeek}
          pickerDate={search().add}
          pickerMeal={search().meal}
          pickerQuery={search().addq}
          onPickerChange={syncPicker}
        />
      </AppErrorBoundary>
    </PageContainer>
  );
}

export const plannerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planner",
  validateSearch: (search: Record<string, unknown>): PlannerSearch => ({
    week: typeof search.week === "string" && isDateKey(search.week) ? search.week : undefined,
    add: typeof search.add === "string" && isDateKey(search.add) ? search.add : undefined,
    meal: isMealType(search.meal) ? search.meal : undefined,
    addq: typeof search.addq === "string" && search.addq.trim() !== "" ? search.addq : undefined,
  }),
  component: PlannerPage,
});
